import * as React from "react"
import { createPortal } from "react-dom"

export type ModalShellProps = {
  ariaDescribedby?: string
  ariaLabelledby?: string
  children: React.ReactNode
  className?: string
  closeButtonClassName?: string
  closeButtonLabel?: string
  initialFocusRef?: { current: HTMLElement | null }
  isOpen?: boolean
  onClose?: () => void
  overlayClassName?: string
  showCloseButton?: boolean
}

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ")
}

export const ModalShell: React.FC<ModalShellProps> = ({
  ariaDescribedby,
  ariaLabelledby,
  children,
  className,
  closeButtonClassName,
  closeButtonLabel = "Close modal",
  initialFocusRef,
  isOpen = false,
  onClose,
  overlayClassName,
  showCloseButton = true,
}) => {
  const onCloseRef = React.useRef(onClose)
  const initialFocusTargetRef = React.useRef(initialFocusRef)
  const previousActiveElementRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  React.useEffect(() => {
    initialFocusTargetRef.current = initialFocusRef
  }, [initialFocusRef])

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined
    }

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const frameId = window.requestAnimationFrame(() => {
      initialFocusTargetRef.current?.current?.focus()
    })

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") {
        return
      }

      event.preventDefault()
      onCloseRef.current?.()
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      window.cancelAnimationFrame(frameId)
      document.body.style.overflow = originalOverflow

      const previousActiveElement = previousActiveElementRef.current

      if (previousActiveElement?.isConnected) {
        previousActiveElement.focus()
      }
    }
  }, [isOpen])

  if (!isOpen || typeof document === "undefined") {
    return null
  }

  const overlayClasses = [
    "fixed inset-0 z-[90] flex items-center justify-center bg-scrim-900 px-4 py-6",
    overlayClassName,
  ]
    .filter(Boolean)
    .join(" ")

  const panelClasses = [
    "relative w-full max-w-[calc(100vw-32px)] outline-none",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return createPortal(
    <div
      className={overlayClasses}
      onClick={event => {
        if (event.target === event.currentTarget) {
          onCloseRef.current?.()
        }
      }}
    >
      <div
        aria-describedby={ariaDescribedby}
        aria-labelledby={ariaLabelledby}
        aria-modal="true"
        className={panelClasses}
        role="dialog"
      >
        {showCloseButton ? (
          <button
            aria-label={closeButtonLabel}
            className={joinClasses(
              "absolute right-[11px] top-[11px] z-10 flex size-[25px] cursor-pointer items-center justify-center rounded-full border-2 border-ink-950 text-ink-950 outline-offset-2 lg:right-[12px] lg:top-[12px] lg:size-[35px]",
              closeButtonClassName,
            )}
            onClick={() => onCloseRef.current?.()}
            type="button"
          >
            <span aria-hidden="true" className="relative block size-full">
              <span className="absolute left-1/2 top-1/2 h-[2px] w-[12px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-ink-950 lg:w-[16px]" />
              <span className="absolute left-1/2 top-1/2 h-[2px] w-[12px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-ink-950 lg:w-[16px]" />
            </span>
          </button>
        ) : null}

        {children}
      </div>
    </div>,
    document.body,
  )
}